import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import type { FieldDefinition, FormLayout, FormPrefillSettings, FormSpamSettings, FormWidget } from '../../shared/types';
import { asksNothing, continuationParents, isContinuation } from '../../shared/types';
import { prefillRowProblems, prefillTargets } from './PrefillLinkCard';
import LinkPanel from './LinkPanel';
import FieldPicker from './FieldPicker';
import { OptGroup, SecInfo, pickerGroups, sectionsFromLayout, sectionsFromSpine } from './fieldGroups';
import { ParentForm, parentOptGroups, parentsLacking } from './parentFields';
import type { ClaimRequirement, FormAccess, FormContinuation } from '../../shared/types';
import {
  AccessProvider, FormSummary, getFormAccess, getFormBuilder, getFormParents, listFormSubmissions, listForms,
  saveFormAccess, updateFormApi,
} from '../../api/forms';
import { PlanLimitBody, planLimitOf, setFormDisabledApi } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { useFormWorkspace } from './FormWorkspace';
import { DomainChips } from './SsoShared';
import { SaveBar, SectionLabel, Toggle } from '../../components/fs';

/** The same default the server and the public form use (U6). */
const DEFAULT_DUPLICATE_MESSAGE = "It looks like you've already responded.";

/**
 * Form → Access: everything that decides whether a given person, at a given
 * moment, gets to fill this form in.
 *
 * Distribute › Link & access (R7, screen 16): the link card first, then two
 * halves of one question — WHO may open it (anonymous, password, SSO, magic
 * link, invite) and WHEN it is open at all (schedule and response cap —
 * moved here from Settings, where it sat under a heading nobody looking for
 * "how do I close this form" would think to open) — with Disable form as the
 * danger row at the end.
 *
 * Deliberately not the same question as how the account's own team signs in to
 * Form-Magic, which is why the intro says so out loud. Access is operational,
 * not part of the form definition: it saves live rather than riding the draft,
 * because locking a form down should never wait for a publish.
 */

/** An ISO datetime as a datetime-local input value (browser-local time). */
function isoToLocal(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** A datetime-local value back to ISO — carries the admin's timezone along. */
function localToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** "30 Sep 2026, 17:00" — a date somebody reads, not an ISO string. */
function whenText(local: string): string {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** How the audience rule reads in a sentence about this form. */
const AUDIENCE: Record<string, string> = {
  open: 'open to anyone with the link',
  password: 'open to anyone with the link and the password',
  sso: 'open to people who sign in with your company account',
  email: 'open to people who verify their email address',
  invite: 'open only to somebody holding an invite link',
};

export interface AvailabilityFacts {
  disabled: boolean;
  mode: string;
  /** datetime-local strings, as the inputs hold them */
  opensAt: string;
  closesAt: string;
  /** the quota as typed; anything not a positive integer is no quota */
  maxSubmissions: string;
  /** how many have arrived, or null while it is still being counted */
  count: number | null;
}

/**
 * The whole point of the card: what is true RIGHT NOW, in one sentence.
 *
 * Audience first, then the first rule that will close it — because "who can
 * fill this in" and "until when" are the two halves of the only question
 * anybody opens this page with, and they were previously spread over two tabs
 * and four controls that each stated a fragment.
 *
 * Everything here is DERIVED. Nothing on this page stores a state; the switch
 * below is the only override, and it beats every rule named here.
 */
export function availabilitySentence(f: AvailabilityFacts): string {
  const n = f.count;
  const collected = n === null
    ? 'Answers already collected are'
    : `The ${n} ${n === 1 ? 'submission' : 'submissions'} already collected ${n === 1 ? 'is' : 'are'}`;
  if (f.disabled) {
    return `Right now: nobody can open this form. The link shows your closed message. ${collected} `
      + 'untouched, and the schedule below is ignored until you switch it back on.';
  }

  const audience = AUDIENCE[f.mode] || AUDIENCE.open;
  const quota = Number(f.maxSubmissions);
  const hasQuota = Number.isInteger(quota) && quota > 0;
  const opens = f.opensAt ? new Date(f.opensAt) : null;
  const closes = f.closesAt ? new Date(f.closesAt) : null;
  const now = Date.now();

  // Not open yet is its own state, and it outranks everything below: saying
  // "closes on the 30th" about a form nobody can reach until the 25th answers
  // a question nobody asked.
  if (opens && !Number.isNaN(opens.getTime()) && now < opens.getTime()) {
    return `Right now: closed until ${whenText(f.opensAt)}, when it becomes ${audience}.`;
  }

  const closesSoon = closes && !Number.isNaN(closes.getTime());
  const parts: string[] = [`Right now: ${audience}.`];
  if (closesSoon && hasQuota) {
    parts.push(`Closes ${whenText(f.closesAt)}, or once ${quota} submissions have arrived — whichever comes first.`);
  } else if (closesSoon) {
    parts.push(`Closes ${whenText(f.closesAt)}.`);
  } else if (hasQuota) {
    parts.push(`Closes once ${quota} submissions have arrived.`);
  } else {
    parts.push('Nothing below will close it — it stays open until you disable it.');
  }
  if (n !== null && (closesSoon || hasQuota)) parts.push(`${n} in so far.`);
  return parts.join(' ');
}

export default function FormAccessTab() {
  const { form, refreshForm, draft } = useFormWorkspace();
  const [access, setAccess] = useState<FormAccess | null>(null);
  const [providers, setProviders] = useState<AccessProvider[]>([]);
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  /** The published layout and widgets — the picker arrangement when there is no draft to read. */
  const [layout, setLayout] = useState<FormLayout | null>(null);
  const [widgets, setWidgets] = useState<FormWidget[]>([]);
  const [accountSlug, setAccountSlug] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [ssoOff, setSsoOff] = useState(false);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const s0 = form.settings.schedule || {};
  const [opensAt, setOpensAt] = useState(isoToLocal(s0.opensAt));
  const [closesAt, setClosesAt] = useState(isoToLocal(s0.closesAt));
  const [maxSubmissions, setMaxSubmissions] = useState(s0.maxSubmissions ? String(s0.maxSubmissions) : '');
  const [closedMessage, setClosedMessage] = useState(s0.closedMessage || '');
  /** What this form carries over from the submission it continues (the order handoff). */
  const [continuation, setContinuation] = useState<FormContinuation | null>(form.settings.continuation ?? null);
  const [otherForms, setOtherForms] = useState<FormSummary[]>([]);
  /**
   * The forms this one continues, with their questions, and the forms whose
   * follow-up link points here. The second is what "any form that links to
   * it" resolves to, and it is shown either way: a form somebody listed but
   * nothing links to, or one that links here but is not listed, is a setup
   * half-done, and this tab is where that gets noticed.
   */
  const [parents, setParents] = useState<ParentForm[]>([]);
  const [linking, setLinking] = useState<{ id: number; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /**
   * The master switch, and the plan gate behind it.
   *
   * It is deliberately NOT part of `snapshot` or the Save button: disabling a
   * form is the one thing on this page somebody does in a hurry, and making it
   * wait behind a Save — or worse, be lost by leaving the tab — would be the
   * wrong failure on the wrong control. It writes on the click.
   */
  const [disabling, setDisabling] = useState(false);
  const [disableLimit, setDisableLimit] = useState<PlanLimitBody | null>(null);
  /** How many have arrived — for the derived sentence. null until counted. */
  const [count, setCount] = useState<number | null>(null);
  /** Bot protection (U6): the form's switch, and whether the platform can honour it. */
  const [spam, setSpam] = useState<FormSpamSettings>(form.settings.spam || {});
  const [captchaConfigured, setCaptchaConfigured] = useState(true);
  /** Which link parameters fill which questions (P1), and the origin the link builder composes from. */
  const [prefill, setPrefill] = useState<FormPrefillSettings | null>(null);
  const [publicBaseUrl, setPublicBaseUrl] = useState<string | null>(null);
  const [platformBaseUrl, setPlatformBaseUrl] = useState<string | null>(null);
  const toast = useToast();

  /**
   * Access saves live rather than riding the draft, so nothing else on the
   * workspace is holding these edits: leaving the tab with a schedule typed
   * in and unsaved loses it outright.
   */
  const snapshot = useMemo(
    () => ({ access, opensAt, closesAt, maxSubmissions, closedMessage, continuation, password, clearPassword, spam, prefill }),
    [access, opensAt, closesAt, maxSubmissions, closedMessage, continuation, password, clearPassword, spam, prefill]
  );
  const { dirty, markSaved } = useDirty(snapshot, !!access);
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: 'Your changes to who can open this form',
    onSave: () => save(),
  });

  useEffect(() => {
    getFormAccess(form.id)
      .then((r) => {
        setAccess({ ...r.access, mode: r.effectiveMode });
        setProviders(r.providers);
        setHasPassword(r.hasPassword);
        setSsoOff(!!r.ssoOff);
        setAccountSlug(r.accountSlug);
        setCaptchaConfigured(r.captchaConfigured !== false);
        if (r.spam) setSpam(r.spam);
        setPrefill(r.prefill?.params.length ? r.prefill : null);
        setPublicBaseUrl(r.formLinkBaseUrl || r.publicBaseUrl || null);
        setPlatformBaseUrl(r.publicBaseUrl || null);
      })
      .catch((e) => setError(e.message));
    getFormBuilder(form.id).then((b) => { setFields(b.fields); setLayout(b.layout); setWidgets(b.widgets || []); }).catch(() => undefined);
    listForms().then((r) => setOtherForms(r.forms.filter((f) => f.id !== form.id))).catch(() => undefined);
    // Only the total is wanted, so ask for one row and read the count beside
    // it. `FormDetail` deliberately drops `submission_count` — it is a list
    // concern — and the derived sentence needs it to say "88 in so far".
    listFormSubmissions(form.id, 1, 1).then((r) => setCount(r.total)).catch(() => setCount(null));
  }, [form.id]);

  // The parents' questions, so a mapping is picked from both ends rather than
  // typed. Reloaded whenever the chosen parents change — the list being
  // chosen, not the saved one, so the picker keeps up with the checkboxes.
  const chosen = continuationParents(continuation);
  const chosenKey = chosen.anyParent ? 'any' : chosen.ids.join(',');
  useEffect(() => {
    let live = true;
    getFormParents(form.id, chosen.anyParent ? undefined : chosen.ids)
      .then((r) => { if (live) { setParents(r.parents || []); setLinking(r.linking || []); } })
      .catch(() => { if (live) { setParents([]); setLinking([]); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, chosenKey]);

  // Where each question lives, so every picker on this tab reads like the
  // form — the Logic page's arrangement, off the same draft, with the
  // published layout standing in when there is no draft.
  const sections = useMemo<SecInfo[]>(
    () => (draft ? sectionsFromSpine(draft.sections, draft.fields) : sectionsFromLayout(layout)),
    [draft, layout]
  );
  const answerable = (f: FieldDefinition) => !asksNothing(f.input_type) && f.input_type !== 'file';
  /** Every answer on this form, arranged; the claim mapping and the once-per-email pick from it. */
  const fieldGroups = useMemo<OptGroup[]>(() => pickerGroups(fields.filter(answerable), sections, widgets), [fields, sections, widgets]);
  const emailGroups = useMemo<OptGroup[]>(
    () => pickerGroups(fields.filter((f) => f.input_type === 'email'), sections, widgets), [fields, sections, widgets]
  );
  /**
   * Order-scope answers on each end of the handoff — what a continuation can
   * carry. The parents' side is the union by key: one row serves every
   * parent that has the key, and reads blank for the orders from one that
   * does not, which the row says beneath itself.
   */
  const parentGroups = useMemo<OptGroup[]>(
    () => parentOptGroups(parents, { prefix: '', subs: false }), [parents]
  );
  const ownGroups = useMemo<OptGroup[]>(
    () => pickerGroups(fields.filter(answerable), sections, [], undefined, { repeat: false }), [fields, sections]
  );

  const provider = useMemo(
    () => providers.find((p) => p.id === access?.providerId) || null,
    [providers, access?.providerId]
  );

  if (!access) {
    return <div className="fs-card pad">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;
  }

  const set = (patch: Partial<FormAccess>) => setAccess({ ...access, ...patch });
  const emailFields = fields.filter((f) => f.input_type === 'email');
  /** A captcha is only ever asked of anonymous respondents. */
  const captchaUsable = captchaConfigured && (access.mode === 'open' || access.mode === 'password');

  async function save() {
    setSaving(true); setError('');
    try {
      // The link mapping is checked here exactly as the server will check it,
      // so a row it would refuse never leaves the screen half-saved.
      const prefillRows = (prefill?.params || []).filter((r) => r.param.trim() || r.field);
      const badRow = prefillRowProblems(prefillRows, prefillTargets(fields)).find(Boolean);
      if (badRow) throw new Error(`Prefill from the link: ${badRow}`);
      // Schedule first, then access: the access route rebuilds settings from
      // the stored row, so writing it second keeps both.
      await updateFormApi(form.id, {
        settings: {
          schedule: {
            opensAt: localToIso(opensAt),
            closesAt: localToIso(closesAt),
            maxSubmissions: Number(maxSubmissions) > 0 ? Number(maxSubmissions) : undefined,
            closedMessage: closedMessage.trim() || undefined,
          },
          // A row with no destination field chosen is not a mapping; the
          // server drops it either way, and sending it would leave the screen
          // showing something that was never stored.
          // Written in the list spelling whatever spelling it was read in, so
          // a row saved from here never carries the old single id as well.
          continuation: continuation && isContinuation(continuation)
            ? {
              parentFormIds: chosen.anyParent ? [] : chosen.ids,
              ...(chosen.anyParent ? { anyParent: true } : {}),
              map: (continuation.map || []).filter((m) => m.from && m.to),
              ...(continuation.confirmPrompt ? { confirmPrompt: continuation.confirmPrompt } : {}),
            }
            : null,
          // Off is absent, not false: the server drops an empty block.
          spam: spam.captcha ? { captcha: true } : null,
        },
      });
      const r = await saveFormAccess(form.id, {
        access,
        // Off is null, not an empty list: the server drops the block.
        prefill: prefillRows.length ? { params: prefillRows } : null,
        ...(clearPassword ? { accessPasswordCleared: true } : {}),
        ...(password ? { accessPassword: password } : {}),
      });
      setAccess(r.access);
      setHasPassword(r.hasPassword);
      setPrefill(r.prefill?.params.length ? r.prefill : null);
      setPassword(''); setClearPassword(false);
      await refreshForm();
      markSaved();
      toast('Access saved — in force now.');
    } catch (e: any) {
      setError(e.message);
      throw e;   // so "Save and leave" stays put rather than losing the edit
    }
    finally { setSaving(false); }
  }

  /**
   * The master switch. Writes on the click, through its own endpoint.
   *
   * `PATCH /:id/disabled` rather than `updateFormApi`, because this is the
   * same switch billing uses when an account drops below its form ceiling —
   * and so re-enabling can be REFUSED with a 402. That refusal is rendered
   * here rather than swallowed: an owner who ticks a box, sees nothing happen
   * and finds the form still closed has been lied to by the interface.
   */
  async function setDisabled(next: boolean) {
    setDisabling(true); setError(''); setDisableLimit(null);
    try {
      await setFormDisabledApi(form.id, next);
      await refreshForm();
      toast(next
        ? 'Form disabled — nobody can open it, and the answers you have are kept.'
        : 'Form enabled — it is taking submissions again, subject to the rules below.');
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit) setDisableLimit(limit);
      else setError(e.message || 'Could not change whether this form is taking submissions.');
    } finally { setDisabling(false); }
  }

  const disabled = !!form.disabled_at;
  const rightNow = availabilitySentence({
    disabled, mode: access.mode, opensAt, closesAt, maxSubmissions, count,
  });

  // Claim names come from the provider's discovery document plus anything typed
  // by hand — no test sign-in needed to configure a rule.
  const claimOptions = provider?.claimNames || [];
  const scheduled = !!(opensAt || closesAt || maxSubmissions);

  /** The design's name for each mode, with the real provider's name where it has one. */
  const ssoTitle = provider ? `People signed in with ${provider.name}` : 'People who sign in with your company account';

  return (
    <div className="la-page">
      {error && <div className="ad-err">{error}</div>}
      {/* Set to single sign-on on a plan without it: say what respondents get
          instead. Rendered only then, so the tab is otherwise as it was. */}
      {ssoOff && access.mode === 'sso' && (
        <div className="fs-consequence warn" role="status" style={{ marginBottom: 14 }}>
          <b>Single sign-on is not part of your plan right now.</b>{' '}
          {hasPassword
            ? 'Until it is, this form asks for its shared password instead.'
            : 'This form has no shared password to fall back to, so it is not accepting responses.'}{' '}
          Your sign-in settings are kept. Choose another way in below, or move to a plan with single sign-on to bring it back.
        </div>
      )}

      <div className="la-grid">
        <div className="la-col">
          {/* The link card — Link · Embed · QR code · Prefilled link (R7). */}
          <section className="fs-card la-linkcard">
            <LinkPanel
              form={{ id: form.id, name: form.name, slug: form.slug }} accountSlug={accountSlug}
              publicBaseUrl={publicBaseUrl} platformBaseUrl={platformBaseUrl} accessMode={access?.mode}
              published={form.status === 'published'}
              prefill={{ formId: form.id, fields, sections, widgets, value: prefill, onChange: setPrefill, saved: !dirty }}
            />
          </section>

          <section className="fs-card">
            <div className="fs-card-head">
              <h2>Who can open it</h2>
              <div className="hint">
                Pick one. This is about the people <b>filling it in</b> — nothing here affects how your own team
                signs in to Form-Magic. Anything stricter than a link is checked before the form is even rendered.
              </div>
            </div>
            <div className="fs-card-body">
              <div className="la-modes" role="radiogroup" aria-label="Who can open it">
                <Choice
                  on={access.mode === 'open'} title="Anyone with the link"
                  hint="No sign-in. Submissions are anonymous unless the form asks for a name."
                  onPick={() => set({ mode: 'open' })}
                />
                <Choice
                  on={access.mode === 'password'} title="Anyone with the password"
                  hint="Everyone types the same password; no identity is captured."
                  onPick={() => set({ mode: 'password' })}
                />
                {access.mode === 'password' && (
                  <div className="fs-choice-body">
                    <div className="fs-field" style={{ maxWidth: 380 }}>
                      <label htmlFor="fa-password">Password {hasPassword ? '(currently set 🔒)' : ''}</label>
                      <input
                        id="fa-password" type="password"
                        placeholder={hasPassword ? 'Enter a new password to change it' : 'Set a password'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (e.target.value) setClearPassword(false); }}
                      />
                      {hasPassword && (
                        <label className="fs-check" style={{ marginTop: 8 }}>
                          <input type="checkbox" checked={clearPassword}
                            onChange={(e) => { setClearPassword(e.target.checked); if (e.target.checked) setPassword(''); }} />
                          <span><b>Remove the password</b></span>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                <Choice
                  on={access.mode === 'sso'} title={ssoTitle}
                  hint="Single sign-on: people sign in with their own company account before they see the form. Each submission carries a verified identity."
                  onPick={() => set({ mode: 'sso', providerId: access.providerId || providers[0]?.id })}
                />
                {access.mode === 'sso' && (
                  <div className="fs-choice-body fs-stack">
                    {providers.length === 0 ? (
                      <div className="fs-consequence warn">
                        This account has no identity providers yet.{' '}
                        <Link to="/admin/settings/access/providers/new">Add one</Link> before requiring sign-in here.
                      </div>
                    ) : (
                      <>
                        <div className="fs-row">
                          <div className="fs-field">
                            <label htmlFor="fa-provider">Identity provider</label>
                            <select id="fa-provider" value={String(access.providerId || '')} onChange={(e) => set({ providerId: Number(e.target.value) })}>
                              <option value="">— pick a provider —</option>
                              {providers.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} — {p.protocol === 'saml' ? 'SAML' : 'OIDC'}
                                  {p.status === 'live' ? ', live sign-ins working' : p.status === 'checks_passed' ? ', checks passed' : p.status === 'failing' ? ', checks failing' : ', not checked'}
                                </option>
                              ))}
                            </select>
                            <div className="hint">
                              From your account's library — sign-in returns to <b>this</b> form.{' '}
                              <Link to="/admin/settings/access/providers/new">+ Add a provider</Link>
                            </div>
                          </div>
                          <div className="fs-field">
                            <label htmlFor="fa-btn-label">Sign-in button label</label>
                            <input
                              id="fa-btn-label" value={access.buttonLabel || ''}
                              placeholder={provider ? `Continue with ${provider.name}` : 'Continue with SSO'}
                              onChange={(e) => set({ buttonLabel: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="fs-field">
                          <label>Extra requirements for this form</label>
                          <RequirementRows
                            rows={access.requirements || []}
                            claims={claimOptions}
                            onChange={(rows) => set({ requirements: rows })}
                          />
                          <div className="hint">
                            Claim names come from the provider's discovery document, plus any you add by hand — no test sign-in required.
                          </div>
                        </div>

                        <Stamp access={access} set={set} what="the verified identity"
                          detail="Email, name, provider and subject id are stored alongside the answers and shown on the submission." />

                        <div className="fs-field">
                          <SectionLabel>Fill fields from their account</SectionLabel>
                          <div className="hint" style={{ marginBottom: 8 }}>
                            Prefilled fields are locked on the form and noted as coming from the signed-in account, using the same treatment
                            as a Logic prefill rule. They are re-applied on the server when the form is submitted, so a locked field cannot
                            be edited around.
                          </div>
                          <ClaimMapRows
                            rows={access.claimMap || []}
                            claims={claimOptions}
                            groups={fieldGroups}
                            onChange={(rows) => set({ claimMap: rows })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                <Choice
                  on={access.mode === 'magic_link'} title="People who confirm their email"
                  hint="For people with no company sign-in: they enter an email, we send a short-lived link. Verified email, no provider needed."
                  onPick={() => set({ mode: 'magic_link' })}
                />
                {access.mode === 'magic_link' && (
                  <div className="fs-choice-body fs-stack">
                    <div className="fs-row">
                      <div className="fs-field">
                        <label htmlFor="fa-ttl">Link expires after</label>
                        <select
                          id="fa-ttl" value={String(access.magicLink?.ttlMinutes ?? 30)}
                          onChange={(e) => set({ magicLink: { ttlMinutes: Number(e.target.value) } })}
                        >
                          {[15, 30, 60, 240, 1440].map((m) => (
                            <option key={m} value={m}>{m >= 60 ? `${m / 60} hour${m === 60 ? '' : 's'}` : `${m} minutes`}</option>
                          ))}
                        </select>
                        <div className="hint">
                          Short lived, not one time: the link keeps working until it expires,
                          however many times it is opened. Mail-security scanners open links
                          before the recipient does, and a one-time link was already spent by
                          the time they clicked it.
                        </div>
                      </div>
                      <div className="fs-field">
                        <label>Limit to these email domains (optional)</label>
                        <DomainChips value={access.allowedDomains || []} onChange={(v) => set({ allowedDomains: v })} />
                        <div className="hint">
                          An address outside these domains is simply not sent a link — the page never says which addresses exist.
                        </div>
                      </div>
                    </div>
                    <Stamp access={access} set={set} what="the confirmed email"
                      detail="The address they proved is stored alongside the answers and shown on the submission." />
                  </div>
                )}

                <Choice
                  on={access.mode === 'invite'} title="Only people you invite"
                  hint="For a form that continues another one. An Invite connection on the first form mints a private link per order; there is no way in without one."
                  onPick={() => set({ mode: 'invite' })}
                />
                {access.mode === 'invite' && (
                  <div className="fs-choice-body fs-stack">
                    <div className="hint">
                      How long a link lasts belongs to the <b>Invite to continue</b> connection
                      on the form that opens the order — not here.
                    </div>
                    <ParentChoice
                      continuation={continuation}
                      otherForms={otherForms}
                      linking={linking}
                      onChange={setContinuation}
                    />
                    {continuation && isContinuation(continuation) && (
                      <div className="fs-field">
                        <label>Answers to carry across</label>
                        <CarryRows
                          rows={continuation.map || []}
                          parentGroups={parentGroups}
                          ownGroups={ownGroups}
                          parents={parents}
                          onChange={(map) => setContinuation({ ...continuation, map })}
                        />
                        <div className="hint">
                          <b>Locked</b> is shown but cannot be changed, and is re-applied when they submit.
                          <b> Confirm</b> asks them to say whether it is right — the answer AND the verdict are recorded,
                          which is what lets you tell "they checked it" from "they never looked".
                          <b> Editable</b> just fills it in.
                        </div>
                        <div className="hint">
                          Everything else the order answered stays off this form but is still there for its
                          workflow — a step on the <Link to="../workflow">Workflow</Link> tab can send the sales
                          rep's email to Monday, and a branch can test the order type, without the customer
                          ever seeing either.
                        </div>
                      </div>
                    )}
                    <div className="fs-field">
                      <label htmlFor="fa-confirm-prompt">Note above the carried answers (optional)</label>
                      <input
                        id="fa-confirm-prompt"
                        value={continuation?.confirmPrompt || ''}
                        placeholder="Please check the details below before continuing."
                        disabled={!continuation || !isContinuation(continuation)}
                        onChange={(e) => continuation && setContinuation({ ...continuation, confirmPrompt: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        <div className="la-col">
          <section className="fs-card">
            <div className="fs-card-head">
              <h2>
                Availability{' '}
                <span className={`fs-pill ${scheduled ? 'info' : 'neutral'}`} style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                  {scheduled ? 'scheduled' : 'always open'}
                </span>
              </h2>
              <div className="hint">
                Open and close the form on a schedule, or stop it after a number of responses. Leave anything
                blank to skip it. Times are in your timezone.
              </div>
            </div>
            <div className="fs-card-body">
              <div className="fs-row tight la-when">
                <div className="fs-field">
                  <label htmlFor="fa-opens">Opens</label>
                  <input id="fa-opens" type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} />
                </div>
                <div className="fs-field">
                  <label htmlFor="fa-closes">Closes</label>
                  <input id="fa-closes" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
                </div>
              </div>
              <div className="fs-field">
                <label htmlFor="fa-max">Stop after (responses)</label>
                <input id="fa-max" inputMode="numeric" placeholder="e.g. 100"
                  value={maxSubmissions} onChange={(e) => setMaxSubmissions(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
              <div className="fs-field">
                <label htmlFor="fa-closed-msg">Message shown once closed (optional)</label>
                <textarea id="fa-closed-msg" rows={2} placeholder="Registration is full — join the waitlist at example.com."
                  value={closedMessage} onChange={(e) => setClosedMessage(e.target.value)} />
                <div className="hint">
                  Shown after the close date, once the response limit is reached, or while the form is
                  disabled. Before the open date, respondents see the opening time instead.
                </div>
              </div>
              {/* The plain-language summary: what is true RIGHT NOW, derived from everything above and the switch below. */}
              <div className={`fs-consequence la-summary${disabled ? ' danger' : ''}`}>{rightNow}</div>
            </div>
          </section>

          <section className="fs-card">
            <div className="fs-card-head">
              <h2>Spam and repeat responses</h2>
              <div className="hint">
                What keeps a response honest once someone can open the form: a captcha for strangers, and one
                response per person for everybody.
              </div>
            </div>
            <div className="fs-card-body">
              <div className="fs-stack" style={{ gap: 14 }}>
                <div className={`la-toggle-row${captchaUsable ? '' : ' faded'}`}>
                  <div className="text">
                    <b>Ask for a captcha before submitting</b>
                    <small>
                      {!captchaConfigured
                        ? 'Not available yet: the platform has no Cloudflare Turnstile keys. A platform admin adds them under Platform → Settings → Captcha.'
                        : captchaUsable
                          ? 'Cloudflare Turnstile, shown above the Submit button. Most people never have to click anything.'
                          : 'Saved, but not asked in this access mode — signed-in, emailed-link and invited respondents are already identified. It applies again if the form goes back to link or password access.'}
                    </small>
                  </div>
                  <Toggle
                    id="fa-captcha" checked={!!spam.captcha} disabled={!captchaConfigured}
                    onChange={(on) => setSpam({ ...spam, captcha: on || undefined })}
                  />
                </div>
                <div className="fs-row tight">
                  <div className="fs-field">
                    <label htmlFor="fa-once-email">One response per email address</label>
                    <FieldPicker
                      id="fa-once-email" value={access.oncePerEmail || ''} groups={emailGroups} placeholder="Off"
                      onChange={(v) => set({ oncePerEmail: v || undefined })}
                    />
                    <div className="hint">
                      {emailFields.length
                        ? 'Checked against the answer to this question, ignoring case. A second response is refused with the message beside it.'
                        : 'Add an email question to the form to use this.'}
                    </div>
                  </div>
                  <div className="fs-field">
                    <label htmlFor="fa-dup-msg">What a repeat respondent sees</label>
                    <input id="fa-dup-msg" placeholder={DEFAULT_DUPLICATE_MESSAGE}
                      value={access.duplicateMessage || ''}
                      onChange={(e) => set({ duplicateMessage: e.target.value || undefined })} />
                  </div>
                </div>
                <div className="la-toggle-row">
                  <div className="text">
                    <b>Remember this browser after it submits</b>
                    <small>
                      Soft: the same browser sees the message above instead of the form on its next visit. Anyone can
                      clear it or use another device — a courtesy against accidental double entries, not a rule.
                    </small>
                  </div>
                  <Toggle
                    id="fa-once-browser" checked={!!access.oncePerBrowser}
                    onChange={(on) => set({ oncePerBrowser: on || undefined })}
                  />
                </div>
              </div>
            </div>
          </section>

          {/*
            The master switch, last and marked as the danger it is. It is
            deliberately NOT part of the Save: disabling a form is the one
            thing on this page somebody does in a hurry. It writes on the click.
          */}
          <section className={`fs-card la-disable${disabled ? ' on' : ''}`}>
            <div className="fs-card-head">
              <h2>
                Disable form{' '}
                <span className={`fs-pill ${disabled ? 'danger' : 'ok'}`} style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                  {disabled ? 'disabled' : 'taking submissions'}
                </span>
              </h2>
              <div className="hint">The only override — it beats the audience and the availability above. Takes effect on the click, no save needed.</div>
            </div>
            <div className="fs-card-body">
              {disableLimit && (
                <div style={{ marginBottom: 12 }}><PlanLimitBlock body={disableLimit} /></div>
              )}
              <label className="fs-check">
                <input
                  type="checkbox" checked={disabled} disabled={disabling}
                  onChange={(e) => { void setDisabled(e.target.checked); }}
                />
                <span>
                  <b>Disable this form</b>
                  <small>
                    Refuses everybody, whatever the schedule or the audience says. Answers already collected
                    are kept, and the form can be switched back on at any time. This is what Draft and
                    Archived used to mean.
                  </small>
                </span>
              </label>
            </div>
          </section>
        </div>
      </div>

      <SaveBar>
        <button className="fs-btn primary" disabled={saving}
          onClick={() => { void save().catch(() => undefined); }}>
          {saving ? 'Saving…' : 'Save access'}
        </button>
        <span className="note">
          {dirty
            ? 'Unsaved changes on this screen.'
            : 'Saved immediately — no publish needed. Access is not part of the draft.'}
        </span>
      </SaveBar>
      {guard.node}
    </div>
  );
}

/** The two ticks every identity mode offers, worded for what it proved. */
function Stamp({ access, set, what, detail }: {
  access: FormAccess; set: (patch: Partial<FormAccess>) => void; what: string; detail: string;
}) {
  return (
    <div className="fs-stack" style={{ gap: 10 }}>
      <label className="fs-check">
        <input type="checkbox" checked={access.stamp !== false} onChange={(e) => set({ stamp: e.target.checked })} />
        <span><b>Stamp every submission with {what}</b><small>{detail}</small></span>
      </label>
      <label className="fs-check">
        <input type="checkbox" checked={!!access.oncePerPerson} onChange={(e) => set({ oncePerPerson: e.target.checked })} />
        <span>
          <b>One submission per person</b>
          <small>A second attempt is refused and points them at the submission they already sent.</small>
        </span>
      </label>
    </div>
  );
}

function Choice({ on, title, hint, onPick, disabled }: {
  on: boolean; title: string; hint: React.ReactNode; onPick: () => void; disabled?: boolean;
}) {
  return (
    <label className={`fs-choice ${on ? 'on' : ''}`} style={disabled ? { opacity: .55, cursor: 'not-allowed' } : undefined}>
      <input type="radio" checked={on} disabled={disabled} onChange={onPick} />
      <span><b>{title}</b><small>{hint}</small></span>
    </label>
  );
}

/** claim · operator · value · remove — half-finished rows survive until complete. */
function RequirementRows({ rows, claims, onChange }: {
  rows: ClaimRequirement[];
  claims: string[];
  onChange: (rows: ClaimRequirement[]) => void;
}) {
  const set = (i: number, patch: Partial<ClaimRequirement>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <>
      {rows.length === 0 && (
        <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 8 }}>
          No extra requirements — anyone the provider signs in may open this form.
        </div>
      )}
      {rows.map((r, i) => {
        const known = claims.includes(r.claim);
        return (
          <div className="sso-rule" key={i}>
            <input
              className="mono" list="access-claims" value={r.claim} placeholder="groups"
              onChange={(e) => set(i, { claim: e.target.value })}
            />
            <select value={r.op} onChange={(e) => set(i, { op: e.target.value as ClaimRequirement['op'] })}>
              <option value="contains">contains</option>
              <option value="is">is</option>
              <option value="isnot">is not</option>
            </select>
            <input value={r.value} placeholder="federal-orders" onChange={(e) => set(i, { value: e.target.value })} />
            <button className="fc-x" title="Remove this requirement" onClick={() => onChange(rows.filter((_, j) => j !== i))}>×</button>
            {!known && r.claim && (
              <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-muted)', marginTop: -2 }}>
                “{r.claim}” is not one the provider advertises — it will still be checked, and confirmed on the first real sign-in.
              </div>
            )}
          </div>
        );
      })}
      <datalist id="access-claims">{claims.map((c) => <option key={c} value={c} />)}</datalist>
      <button className="fs-btn neutral sm" style={{ marginTop: 8 }} onClick={() => onChange([...rows, { claim: '', op: 'contains', value: '' }])}>
        + Add a requirement
      </button>
    </>
  );
}

/** claim → form field, the same idiom as the Connections mapping rows. */
function ClaimMapRows({ rows, claims, groups, onChange }: {
  rows: { claim: string; field: string }[];
  claims: string[];
  /** the form's questions, arranged the way the form is built */
  groups: OptGroup[];
  onChange: (rows: { claim: string; field: string }[]) => void;
}) {
  const used = new Set(rows.map((r) => r.field).filter(Boolean));
  const set = (i: number, patch: Partial<{ claim: string; field: string }>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  // email and name are always available: they are standard OIDC claims the
  // sign-in already resolved, whatever the provider advertises.
  const options = [...new Set(['email', 'name', ...claims])];

  return (
    <>
      {rows.length === 0 && (
        <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 8 }}>
          Nothing mapped — respondents fill every field in themselves.
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="fa-claim-row">
          <input
            className="fs-input fs-mono fs-w-220" list="prefill-claims"
            value={r.claim} placeholder="email"
            onChange={(e) => set(i, { claim: e.target.value })}
          />
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>→</span>
          <FieldPicker
            className="fs-input fs-w-300" value={r.field} groups={groups} disabledValues={used}
            placeholder="— pick a form field —" onChange={(field) => set(i, { field })}
          />
          <button className="fc-x" title="Remove this mapping" onClick={() => onChange(rows.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <datalist id="prefill-claims">{options.map((c) => <option key={c} value={c} />)}</datalist>
      <button className="fs-btn neutral sm" onClick={() => onChange([...rows, { claim: '', field: '' }])}>+ Add mapping</button>
    </>
  );
}

/**
 * The field-by-field mapping from the parent form's answers to this one's.
 *
 * Both ends are pickers rather than typed keys: a mapping that names a field
 * which does not exist carries nothing across, silently, and the person filling
 * the form in is the one who finds out.
 */
/**
 * Which forms this one continues: nothing, any that links here, or a list.
 *
 * The list is a guardrail rather than the credential — the invite link is
 * that — so "any form that links here" is the honest default for a child
 * shared by several order forms, and the list is for the account that wants
 * to be told when a link arrives from a form nobody meant.
 */
function ParentChoice({ continuation, otherForms, linking, onChange }: {
  continuation: FormContinuation | null;
  otherForms: FormSummary[];
  linking: { id: number; name: string }[];
  onChange: (c: FormContinuation | null) => void;
}) {
  const p = continuationParents(continuation);
  const mode: 'none' | 'any' | 'listed' = p.anyParent ? 'any' : p.ids.length ? 'listed' : 'none';
  const base = { map: continuation?.map || [], ...(continuation?.confirmPrompt ? { confirmPrompt: continuation.confirmPrompt } : {}) };
  const linkingIds = new Set(linking.map((f) => f.id));
  const linkingNames = linking.map((f) => f.name).join(', ');
  const toggle = (id: number, on: boolean) => onChange({
    ...base, parentFormIds: on ? [...p.ids, id] : p.ids.filter((x) => x !== id),
  });

  return (
    <div className="fs-field">
      <label>This form continues</label>
      <div role="radiogroup" aria-label="Which forms this one continues">
        <Choice
          on={mode === 'none'} title="Nothing — it stands alone"
          hint="A link opens it, but the submission is not the second half of anything."
          onPick={() => onChange(null)}
        />
        <Choice
          on={mode === 'any'} title="Any form that links here"
          hint={<>
            Whichever form's follow-up link opened it is the order it continues — one form behind every
            partner's order form. {linking.length
              ? <>Right now that is <b>{linkingNames}</b>.</>
              : <>No form links here yet; add a <b>Follow-up link</b> step to one and it will.</>}
          </>}
          onPick={() => onChange({ ...base, parentFormIds: [], anyParent: true })}
        />
        <Choice
          on={mode === 'listed'} title="Only these forms"
          hint="A link from any other form still opens it, but the submission continues nothing and carries nothing."
          onPick={() => onChange({ ...base, parentFormIds: p.ids.length ? p.ids : linking.slice(0, 1).map((f) => f.id) })}
        />
      </div>
      {mode === 'listed' && (
        <div className="fs-choice-body fs-stack">
          {otherForms.map((f) => (
            <label key={f.id} className="fs-check">
              <input type="checkbox" checked={p.ids.includes(f.id)} onChange={(e) => toggle(f.id, e.target.checked)} />
              <span>
                <b>{f.name}</b>
                {linkingIds.has(f.id)
                  ? <small>links here</small>
                  : p.ids.includes(f.id) ? <small>listed, but nothing on it links here yet</small> : null}
              </span>
            </label>
          ))}
          {!otherForms.length && <div className="hint">This account has no other forms yet.</div>}
        </div>
      )}
    </div>
  );
}

function CarryRows({ rows, parentGroups, ownGroups, parents, onChange }: {
  rows: FormContinuation['map'];
  /** the parents' order-scope answers (the union), and this form's, each arranged the way its form is built */
  parentGroups: OptGroup[];
  ownGroups: OptGroup[];
  /** the parents themselves, to say which of them a picked answer is missing from */
  parents: ParentForm[];
  onChange: (rows: FormContinuation['map']) => void;
}) {
  const patch = (i: number, p: Partial<FormContinuation['map'][number]>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  return (
    <>
      {!rows.length && (
        <div className="hint">Nothing is carried across yet — this form will ask for everything from scratch.</div>
      )}
      {rows.map((row, i) => {
        const lacking = row.from ? parentsLacking(parents, row.from) : [];
        return (
          <div key={i}>
            <div className="fa-carry-row">
              <FieldPicker
                value={row.from} className="fa-carry-from" groups={parentGroups} placeholder="— from the order —"
                onChange={(from) => patch(i, { from })}
              />
              <span aria-hidden>→</span>
              <FieldPicker
                value={row.to} className="fa-carry-to" groups={ownGroups} placeholder="— shown here as —"
                onChange={(to) => patch(i, { to })}
              />
              <select
                value={row.mode} className="fa-carry-mode"
                onChange={(e) => patch(i, { mode: e.target.value as FormContinuation['map'][number]['mode'] })}
              >
                <option value="locked">Locked</option>
                <option value="confirm">Confirm</option>
                <option value="editable">Editable</option>
              </select>
              <button className="ad-btn small danger" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                &times;
              </button>
            </div>
            {lacking.length > 0 && lacking.length < parents.length && (
              <div className="hint fa-carry-note">
                Not on {lacking.map((f) => `"${f.name}"`).join(', ')} — an order from there carries nothing for this row.
              </div>
            )}
          </div>
        );
      })}
      <button
        className="ad-btn small secondary"
        onClick={() => onChange([...rows, { from: '', to: '', mode: 'confirm' }])}
      >+ Carry another answer</button>
    </>
  );
}
